import { useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  getInvestorsByContact,
  getEntitiesByInvestor,
  getReportDatesForAccountOrContact,
  getAssetSummaryAndAllocation
} from "../services/assetOverviewService";
import "../styles/assetOverview.css";
import AssetSummaryGrid from "../asset_UI_components/AssetSummaryGrid";
import AssetAllocationChart from "../asset_UI_components/AssetAllocationChart";
import PerformanceSummaryGrid from "../asset_UI_components/PerformanceSummaryGrid";
import useDocumentTitle from "../../../shared/customHooks/useDocumentTitle";
import { LoadingSpinner } from "../../../shared/components/LoadingSpinner";
import ToolbarSelect from "./ToolbarSelect";

const get = (obj, camel, pascal) => obj?.[camel] ?? obj?.[pascal];

const AssetOverview = () => {
  useDocumentTitle("Asset Overview");

  const { contactId: contactIdParam } = useParams();
  const contactId = Number(atob(contactIdParam));

  const [isLoading, setIsLoading] = useState(true);

  const [investors, setInvestors] = useState([]);
  const [entities, setEntities] = useState([]);
  const [reportDates, setReportDates] = useState([]);

  const [selectedInvestor, setSelectedInvestor] = useState("");
  const [selectedEntity, setSelectedEntity] = useState("");
  const [selectedDate, setSelectedDate] = useState("");

  const [assetSummary, setAssetSummary] = useState([]);
  const [allocationData, setAllocationData] = useState([]);
  const [performanceSummary, setPerformanceSummary] = useState([]);

  const selectedEntityObj = useMemo(
    () => entities.find((e) => String(get(e, "entityId", "EntityId")) === String(selectedEntity)),
    [entities, selectedEntity]
  );
  const investorOptions = useMemo(
    () =>
      investors.map((inv) => ({
        value: get(inv, "investorId", "InvestorId"),
        label: get(inv, "investorName", "InvestorName"),
      })),
    [investors]
  );
  const entityOptions = useMemo(
    () =>
      entities.map((ent) => ({
        value: get(ent, "entityId", "EntityId"),
        label: get(ent, "entityName", "EntityName"),
      })),
    [entities]
  );
  const reportDateOptions = useMemo(
    () =>
      reportDates.map((d) => ({
        value: get(d, "reportDate", "ReportDate"),
        label: get(d, "reportDate", "ReportDate"),
      })),
    [reportDates]
  );

  const didInitRef = useRef(false);

  const resolveAccountContact = (entity) => {
    const entityTypeId = Number(get(entity || {}, "entityTypeId", "EntityTypeId") || 0);
    const entityId = Number(get(entity || {}, "entityId", "EntityId") || 0);
    return entityTypeId === 1
      ? { accountId: entityId, contactEntityId: 0 }
      : { accountId: 0, contactEntityId: entityId };
  };

  const loadOverview = async (reportingMonth, accountId, contactEntityId) => {
    if (!reportingMonth) {
      setIsLoading(false);
      return { hasData: false };
    }

    setIsLoading(true);
    try {
      const res = await getAssetSummaryAndAllocation(reportingMonth, accountId, contactEntityId);
      const response = res?.data || {};
      const allocation = response.assetAllocation || [];
      const performance = response.assetPerformanceSummary || [];
      const summary = response.assetAllocation || [];

      setAllocationData(allocation);
      setPerformanceSummary(performance);
      setAssetSummary(summary);

      const hasData = (allocation?.length || 0) > 0 || (performance?.length || 0) > 0;
      return { hasData };
    } finally {
      setIsLoading(false);
    }
  };

  const pickFirstInvestorWithData = async (invList) => {
    for (const inv of invList) {
      const invId = get(inv, "investorId", "InvestorId");
      const entRes = await getEntitiesByInvestor(invId, contactId);
      const ent = entRes?.data || [];
      if (!ent.length) continue;

      for (const entity of ent) {
        const { accountId, contactEntityId } = resolveAccountContact(entity);
        const dateRes = await getReportDatesForAccountOrContact(accountId, contactEntityId);
        const dates = dateRes?.data || [];
        if (!dates.length) continue;

        const firstDate = get(dates[0], "reportDate", "ReportDate");
        const { hasData } = await loadOverview(firstDate, accountId, contactEntityId);

        if (hasData) {
          return {
            investor: inv,
            ent,
            dates,
            firstDate
          };
        }
      }
    }
    return null;
  };

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    if (!contactId || Number.isNaN(contactId)) return;

    const init = async () => {
      setIsLoading(true);

      const invRes = await getInvestorsByContact(contactId);
      const inv = invRes?.data || [];
      setInvestors(inv);

      if (!inv.length) {
        setIsLoading(false);
        return;
      }

      // Try to pick first investor that has data
      const match = await pickFirstInvestorWithData(inv);

      if (match) {
        const invId = get(match.investor, "investorId", "InvestorId");
        const entId = get(match.entity, "entityId", "EntityId");

        setSelectedInvestor(String(invId));
        setEntities(match.ent);
        setSelectedEntity(String(entId));
        setReportDates(match.dates);
        setSelectedDate(match.firstDate);
        return;
      }

      // Fallback = first investor
      const firstInvestorId = get(inv[0], "investorId", "InvestorId");
      setSelectedInvestor(String(firstInvestorId));

      const entRes = await getEntitiesByInvestor(firstInvestorId, contactId);
      const ent = entRes?.data || [];
      setEntities(ent);
      if (!ent.length) {
        setIsLoading(false);
        return;
      }

      const firstEntity = ent[0];
      const firstEntityId = get(firstEntity, "entityId", "EntityId");
      setSelectedEntity(String(firstEntityId));

      const { accountId, contactEntityId } = resolveAccountContact(firstEntity);
      const dateRes = await getReportDatesForAccountOrContact(accountId, contactEntityId);
      const dates = dateRes?.data || [];
      setReportDates(dates);
      if (!dates.length) {
        setIsLoading(false);
        return;
      }

      const firstDate = get(dates[0], "reportDate", "ReportDate");
      setSelectedDate(firstDate);

      await loadOverview(firstDate, accountId, contactEntityId);
    };

    init();
  }, [contactId]);

  const onInvestorChange = async (e) => {
    const investorId = e.target.value;
    setIsLoading(true);
    setSelectedInvestor(investorId);

    const entRes = await getEntitiesByInvestor(investorId, contactId);
    const ent = entRes?.data || [];
    setEntities(ent);
    setAssetSummary([]);
    setAllocationData([]);
    setPerformanceSummary([]);

    if (!ent.length) {
      setSelectedEntity("");
      setReportDates([]);
      setSelectedDate("");
      setIsLoading(false);
      return;
    }

    const firstEntity = ent[0];
    const firstEntityId = get(firstEntity, "entityId", "EntityId");
    setSelectedEntity(String(firstEntityId));

    const { accountId, contactEntityId } = resolveAccountContact(firstEntity);
    const dateRes = await getReportDatesForAccountOrContact(accountId, contactEntityId);
    const dates = dateRes?.data || [];
    setReportDates(dates);

    if (!dates.length) {
      setSelectedDate("");
      setIsLoading(false);
      return;
    }

    const firstDate = get(dates[0], "reportDate", "ReportDate");
    setSelectedDate(firstDate);
    await loadOverview(firstDate, accountId, contactEntityId);
  };

  const onEntityChange = async (e) => {
    const entityId = e.target.value;
    setSelectedEntity(entityId);
    setIsLoading(true);

    const entity = entities.find((x) => String(get(x, "entityId", "EntityId")) === String(entityId));
    if (!entity) {
      setIsLoading(false);
      return;
    }

    const { accountId, contactEntityId } = resolveAccountContact(entity);
    const dateRes = await getReportDatesForAccountOrContact(accountId, contactEntityId);
    const dates = dateRes?.data || [];
    setReportDates(dates);
    setAssetSummary([]);
    setAllocationData([]);
    setPerformanceSummary([]);

    if (!dates.length) {
      setSelectedDate("");
      setIsLoading(false);
      return;
    }

    const firstDate = get(dates[0], "reportDate", "ReportDate");
    setSelectedDate(firstDate);
    await loadOverview(firstDate, accountId, contactEntityId);
  };

  const onDateChange = async (e) => {
    const date = e.target.value;
    setSelectedDate(date);

    if (!selectedEntityObj || !date) {
      setIsLoading(false);
      return;
    }
    const { accountId, contactEntityId } = resolveAccountContact(selectedEntityObj);
    await loadOverview(date, accountId, contactEntityId);
  };

  const handleGo = async () => {
    if (!selectedEntityObj || !selectedDate) {
      setIsLoading(false);
      return;
    }
    const { accountId, contactEntityId } = resolveAccountContact(selectedEntityObj);
    await loadOverview(selectedDate, accountId, contactEntityId);
  };

  return (
    <div className="asset-overview">
      <div className="action-bar custom-fonts">
        <div className="row">
          <div className="col-md-4">
            <label>Investor:</label>
            <ToolbarSelect
              value={selectedInvestor}
              onChange={(nextValue) => onInvestorChange({ target: { value: nextValue } })}
              options={investorOptions}
            />
          </div>

          <div className="col-md-4">
            <label>Entity:</label>
            <ToolbarSelect
              value={selectedEntity}
              onChange={(nextValue) => onEntityChange({ target: { value: nextValue } })}
              options={entityOptions}
            />
          </div>

          <div className="col-md-2">
            <label>Date:</label>
            <ToolbarSelect
              value={selectedDate}
              onChange={(nextValue) => onDateChange({ target: { value: nextValue } })}
              options={reportDateOptions}
            />
          </div>

          <div className="col-md-1">
            <button className="btn2 btn-block focus-remove custom-fonts" onClick={handleGo}>
              Go
            </button>
          </div>
          <div className="col-md-1"></div>
        </div>
      </div>

      <hr />
      <h3 className="AP-style" style={{color:'#10045a'}}>Asset Overview</h3>

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div className="overview-layout">
          <div className="row">
            <div className="col-md-6" style={{height: '476px'}}>
              <div style={{color:'#ffffff', backgroundColor:'#10045a', height:'30px',marginBottom:'5px'}}>
                <h4 style={{paddingTop:'5px', paddingLeft:'5px'}}>Asset Summary</h4></div>
              <AssetSummaryGrid data={assetSummary} />
            </div>
            <div id='assetAllocationChart' className="col-md-6">
              <div style={{color:'#ffffff', backgroundColor:'#10045a', height:'30px',marginBottom:'5px'}}>
                <h4 style={{paddingTop:'5px', paddingLeft:'5px'}}>Asset Allocation</h4></div>
              <AssetAllocationChart data={allocationData} />
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-md-12">
              <div style={{color:'#ffffff', backgroundColor:'#10045a', height:'30px',marginBottom:'5px'}}>
                <h4 style={{paddingTop:'5px', paddingLeft:'5px'}}>Asset Class Performance Summary</h4></div>
              <PerformanceSummaryGrid data={performanceSummary} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetOverview;
